import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Alert, AlertTitle, Button, LinearProgress, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { JobProgress } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DateRangeSelect from "@entrilia/shared/components/DateRangeSelect";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import MultiSelectFilter from "@entrilia/shared/components/MultiSelectFilter";
import useFetch from "@entrilia/shared/hooks/useFetch";
import useJobProgress from "@entrilia/shared/hooks/useJobProgress";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import {
  lastMonth,
  lastQuarter,
  lastYear,
  thisMonth,
  thisQuarter,
  thisYear,
} from "@entrilia/shared/utilities/dateFilterHelper";
import { formatPercentage } from "@entrilia/shared/utilities/formatters";
import adminApi from "../../../../../api/adminApi";

const startActivityLogExport = withErrorHandling(adminApi.startActivityLogExport);

const predefinedDateRanges: DescriptiveDateRange[] = [
  thisMonth,
  thisQuarter,
  thisYear,
  lastMonth,
  lastQuarter,
  lastYear,
];

const ActivityLogExport = () => {
  const [selectedResourceTypes, setSelectedResourceTypes] = useState<string[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<DescriptiveDateRange>();
  const [exporting, setExporting] = useState(false);
  const [fileDownloadUrl, setFileDownloadUrl] = useState<string>();
  const [downloading, setDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [pollingFailed, setPollingFailed] = useState(false);

  const [exportOptions, fetchExportOptionsError] = useFetch(adminApi.getActivityLogExportOptions);

  const handleJobFinished = useCallback((jobProgress: JobProgress) => {
    setExporting(false);

    if (jobProgress.isSuccess) {
      const downloadUrl = jobProgress.properties["DownloadUrl"];
      if (downloadUrl) {
        setFileDownloadUrl(downloadUrl);
      }
    } else {
      setErrorMessage(jobProgress.errorMessage);
    }
  }, []);

  const handlePollingError = useCallback(() => {
    setExporting(false);
    setPollingFailed(true);
  }, []);

  const { jobProgress, startJobProgressCheck } = useJobProgress(adminApi.getActivityLogExportJobProgress, {
    onJobFinished: handleJobFinished,
    onPollingError: handlePollingError,
    updateIntervalMs: 500,
  });

  if (fetchExportOptionsError) {
    logError(fetchExportOptionsError, "[ActivityLogPage] getActivityLogExportOptions");
    return <DataLoadingFailed title="Failed to load activity log export options" />;
  }

  if (exportOptions === undefined) {
    return <InlineLoader />;
  }

  if (exportOptions.length === 0) {
    return <Alert severity="warning">No export options are currently available</Alert>;
  }

  const isValid = selectedResourceTypes.length > 0 && selectedDateRange !== undefined;

  const handleStartExport = async () => {
    if (!selectedDateRange?.from || !selectedDateRange?.to || selectedResourceTypes.length === 0) {
      return;
    }

    setExporting(true);
    setFileDownloadUrl(undefined);
    setErrorMessage(undefined);
    setPollingFailed(false);

    const [jobStartResult, jobStartError] = await startActivityLogExport({
      resourceTypes: selectedResourceTypes,
      startDate: selectedDateRange.from.toISOString(),
      endDate: selectedDateRange.to.toISOString(),
    });

    if (jobStartError) {
      logError(jobStartError, "[ActivityLogExport] startActivityLogExport");
      setErrorMessage(jobStartError.message);
      setExporting(false);
      return;
    }

    startJobProgressCheck(jobStartResult.operationId);
  };

  const handleDownloadFile = async () => {
    if (fileDownloadUrl) {
      setDownloading(true);
      await downloadFileFromUrl(fileDownloadUrl);
      setDownloading(false);
    }
  };

  return (
    <>
      <Stack spacing={3} sx={{ width: "19rem" }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Product Area</Typography>
          {/* Currently the only available product area is Portfolio Monitoring. */}
          <Select value="PortfolioMonitoring" disabled fullWidth>
            <MenuItem value="PortfolioMonitoring">Portfolio Monitoring</MenuItem>
          </Select>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="subtitle2">Resource Type</Typography>
          <MultiSelectFilter
            placeholder="Select resource types"
            options={exportOptions}
            selectedValues={selectedResourceTypes}
            onChange={(values) => setSelectedResourceTypes(values)}
            getOptionValue={(option) => option.resourceType}
            getOptionLabel={(option) => option.displayName}
            sortOptions
            fullWidth
            disabled={exporting}
          />
        </Stack>

        <Stack spacing={1}>
          <Typography variant="subtitle2">Date Range</Typography>
          <DateRangeSelect
            predefinedRanges={predefinedDateRanges}
            dateRange={selectedDateRange}
            onChange={(dateRange) => setSelectedDateRange(dateRange)}
            fullWidth
            disabled={exporting}
          />
        </Stack>

        <Button
          variant="contained"
          onClick={handleStartExport}
          disabled={!isValid || exporting}
          sx={{ width: "fit-content" }}
        >
          Start Export
        </Button>
      </Stack>
      {jobProgress && (
        <Stack spacing={2} sx={{ mt: 4 }}>
          <Alert severity="info">
            <AlertTitle>Preparing your export</AlertTitle>
            We&apos;re generating your file. This may take a few minutes.
            <br />
            Once the file is ready, you&apos;ll be able to download it.
            <br />
            We&apos;ll also send an email with a download link.
          </Alert>

          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <LinearProgress
              variant="determinate"
              value={jobProgress.progressPercent}
              sx={{ width: "calc(100% - 2rem)" }}
            />
            <Typography color="textSecondary">{formatPercentage(jobProgress.progressPercent / 100)}</Typography>
          </Stack>
        </Stack>
      )}
      {pollingFailed && (
        <Stack spacing={2} sx={{ mt: 4 }}>
          <Alert severity="info">
            We couldn&apos;t check the export&apos;s progress. If it&apos;s still running, we&apos;ll email you a
            download link when it&apos;s ready.
          </Alert>
        </Stack>
      )}
      {errorMessage && (
        <Stack spacing={2} sx={{ mt: 4 }}>
          <Alert severity="error">
            <AlertTitle>Export failed</AlertTitle>
            <Typography variant="caption" color="textSecondary">
              {errorMessage}
            </Typography>
          </Alert>
        </Stack>
      )}
      {fileDownloadUrl && (
        <Stack spacing={2} sx={{ mt: 4 }}>
          <Alert severity="success">
            <AlertTitle>Your export is ready!</AlertTitle>
            The file is ready to download.
          </Alert>

          <Button
            variant="contained"
            onClick={handleDownloadFile}
            loading={downloading}
            startIcon={<DownloadIcon />}
            sx={{ width: "fit-content" }}
          >
            Download
          </Button>
        </Stack>
      )}
    </>
  );
};

export default ActivityLogExport;
